import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material'
import {
  Person,
  Email,
  Business,
  Security,
  Save,
  Visibility,
  VisibilityOff,
  Lock,
  CheckCircle,
  Edit
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import axios from 'axios'

const PerfilUsuario = () => {
  const { user, updateProfile } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showPasswordSection, setShowPasswordSection] = useState(false)

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password_actual: '',
    nueva_password: '',
    confirmar_password: ''
  })

  useEffect(() => {
    if (user) {
      setFormData({
        nombre: user.nombre || '',
        email: user.email || '',
        password_actual: '',
        nueva_password: '',
        confirmar_password: ''
      })
    }
  }, [user])

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    })
  }

  const handleUpdateProfile = async () => {
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const updateData = {
        nombre: formData.nombre,
        email: formData.email
      }

      // Solo incluir password si se está cambiando
      if (formData.nueva_password) {
        if (formData.nueva_password !== formData.confirmar_password) {
          setError('Las contraseñas no coinciden')
          setLoading(false)
          return
        }

        if (formData.nueva_password.length < 6) {
          setError('La nueva contraseña debe tener al menos 6 caracteres')
          setLoading(false)
          return
        }

        updateData.password_actual = formData.password_actual
        updateData.nueva_password = formData.nueva_password
      }

      const result = await updateProfile(updateData)

      if (result.success) {
        setSuccess('Perfil actualizado exitosamente')
        setFormData({
          ...formData,
          password_actual: '',
          nueva_password: '',
          confirmar_password: ''
        })
        setShowPasswordSection(false)
      } else {
        setError(result.error || 'Error al actualizar perfil')
      }
    } catch (error) {
      setError('Error al actualizar perfil: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const getRoleBadgeColor = (rol) => {
    switch (rol) {
      case 'administrador': return 'error'
      case 'editor': return 'warning'
      case 'consultor': return 'info'
      default: return 'default'
    }
  }

  const getRoleLabel = (rol) => {
    switch (rol) {
      case 'administrador': return 'Administrador'
      case 'editor': return 'Editor'
      case 'consultor': return 'Consultor'
      default: return rol
    }
  }

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No se pudo cargar la información del usuario</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Person sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Mi Perfil
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Información del Usuario */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader
              title="Información del Usuario"
              avatar={<Person />}
            />
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '2rem',
                  margin: '0 auto 2rem'
                }}
              >
                {user.nombre?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>

              <Typography variant="h6" gutterBottom>
                {user.nombre}
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {user.email}
              </Typography>

              <Chip
                label={getRoleLabel(user.rol)}
                color={getRoleBadgeColor(user.rol)}
                sx={{ mt: 1 }}
              />

              <Divider sx={{ my: 2 }} />

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Estado"
                    secondary={user.activo ? 'Activo' : 'Inactivo'}
                  />
                </ListItem>

                {user.gestiones && user.gestiones.length > 0 && (
                  <ListItem>
                    <ListItemIcon>
                      <Business />
                    </ListItemIcon>
                    <ListItemText
                      primary="Gestiones Asignadas"
                      secondary={
                        user.gestiones.map((gestion) =>
                          `${gestion.codigo} - ${gestion.nombre} ${gestion.puede_editar ? '(Editable)' : '(Solo lectura)'}`
                        ).join(', ')
                      }
                    />
                  </ListItem>
                )}

                {user.ultimo_login && (
                  <ListItem>
                    <ListItemIcon>
                      <Security />
                    </ListItemIcon>
                    <ListItemText
                      primary="Último Acceso"
                      secondary={new Date(user.ultimo_login).toLocaleString('es-CO')}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Formulario de Edición */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Editar Perfil"
              avatar={<Edit />}
            />
            <CardContent>
              <form onSubmit={(e) => { e.preventDefault(); handleUpdateProfile(); }}>
                <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    value={formData.nombre}
                    onChange={handleInputChange('nombre')}
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Correo Electrónico"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={showPasswordSection ? <VisibilityOff /> : <Visibility />}
                    onClick={() => setShowPasswordSection(!showPasswordSection)}
                  >
                    {showPasswordSection ? 'Ocultar' : 'Cambiar'} Contraseña
                  </Button>
                </Grid>

                {showPasswordSection && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Lock /> Cambiar Contraseña
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Contraseña Actual"
                        type="password"
                        value={formData.password_actual}
                        onChange={handleInputChange('password_actual')}
                        required
                        inputProps={{
                          autoComplete: 'current-password'
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nueva Contraseña"
                        type="password"
                        value={formData.nueva_password}
                        onChange={handleInputChange('nueva_password')}
                        required
                        helperText="Mínimo 6 caracteres"
                        inputProps={{
                          autoComplete: 'new-password'
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Confirmar Nueva Contraseña"
                        type="password"
                        value={formData.confirmar_password}
                        onChange={handleInputChange('confirmar_password')}
                        required
                        error={formData.nueva_password !== formData.confirmar_password && formData.confirmar_password !== ''}
                        helperText={
                          formData.nueva_password !== formData.confirmar_password && formData.confirmar_password !== ''
                            ? 'Las contraseñas no coinciden'
                            : ''
                        }
                        inputProps={{
                          autoComplete: 'new-password'
                        }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<Save />}
                      disabled={loading}
                    >
                      {loading ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default PerfilUsuario